/**
 * Field schema of a builder section: what the admin editor and the visual editor in
 * ecommerce render for its props. Both editors build their forms from it, so a section
 * type described here needs no hand-written editor.
 */
interface BaseField {
  /** Key in `section.props`. */
  key: string;
  /** Polish label shown above the input. */
  label: string;
  /** One line under the input. */
  help?: string;
}

export type LandingSectionField =
  | (BaseField & {
      /**
       * `richText` is a string with `<strong>`, `<em>`, `<s>`, `<mark>`, `<br />` and
       * `<span>`, rendered through `sanitizeLandingRichText`. Text fields accept the
       * funnel tokens (`{{cena}}` etc., see `FUNNEL_TEXT_TOKENS`).
       */
      type: "text" | "textarea" | "richText" | "image" | "video" | "url" | "color";
      placeholder?: string;
    })
  | (BaseField & { type: "number" })
  | (BaseField & { type: "boolean" })
  | (BaseField & { type: "select"; options: { label: string; value: string }[] })
  /** Array of strings, one per line in the editor. */
  | (BaseField & { type: "stringList" })
  /** Array of objects, each edited with `fields`. */
  | (BaseField & { type: "list"; itemLabel?: string; fields: LandingSectionField[] });

/** Data a section needs from the funnel step loader on top of the usual step props. */
export type LandingSectionDataNeed = "cohort";

export interface LandingSectionDefinition {
  /** Polish name in the "add section" picker. */
  label: string;
  /** Picker group, e.g. "6 Tygodni do Bikini" or "Checkout". */
  group: string;
  fields: LandingSectionField[];
  /** Props of a newly added section; the built-in templates carry their own copy. */
  defaultProps: Record<string, unknown>;
  needs?: LandingSectionDataNeed[];
}

/**
 * Tokens replaced in section texts with the funnel's data (prices of the funnel
 * product, the Reset group). In a preview without a funnel they show sample values.
 */
export const FUNNEL_TEXT_TOKENS = [
  { token: "{{cena}}", label: "Cena jednorazowa, np. 997 zł" },
  { token: "{{cenaPrzedRabatem}}", label: "Cena przed rabatem (przekreślona), np. 1 050 zł" },
  { token: "{{raty}}", label: "Raty, np. 2× 547 zł" },
  { token: "{{oszczednosc}}", label: "Oszczędność, np. 53 zł" },
  { token: "{{startGrupy}}", label: "Start grupy Resetu, np. 19.10" },
  { token: "{{wolneMiejsca}}", label: "Wolne miejsca w grupie Resetu" },
] as const;
